import { lazy } from 'react';
import { Badge } from '@mui/material';

import { formatDateTime } from '../../../utils/formatting';
import { humanizeValue, str } from '../field-access';
import type { ResourceViewDefinition } from '../types';
import { AppointmentHeader } from './appointment-header';
import { statusTone } from './status-tone';

/**
 * Appointment view registration (GDN-2723) — replaces the standalone
 * appointment detail page and the bespoke appointments list.
 */
export const appointmentView: ResourceViewDefinition = {
  label: 'Appointment',
  header: AppointmentHeader,
  preview: lazy(() =>
    import('./appointment-preview').then(m => ({
      default: m.AppointmentPreview,
    })),
  ),
  sections: lazy(() =>
    import('./appointment-sections').then(m => ({
      default: m.AppointmentSections,
    })),
  ),
  columns: [
    { key: 'title', label: 'Title' },
    {
      key: 'start_time',
      label: 'Start',
      render: value => (typeof value === 'string' ? formatDateTime(value) : '—'),
    },
    {
      key: 'duration',
      label: 'Duration',
      render: value => (typeof value === 'number' ? `${value} min` : '—'),
    },
    {
      key: 'status',
      label: 'Status',
      // Soft-deleted appointments reach admin lists (GDN-2709) and would
      // otherwise be indistinguishable from live ones.
      render: (value, row) => (
        <span className="flex items-center gap-1.5">
          <Badge color={statusTone(typeof value === 'string' ? value : null)}>
            {typeof value === 'string' ? value : '—'}
          </Badge>
          {str(row, 'deleted_at') && (
            <span className="text-xs font-medium text-red-600">deleted</span>
          )}
        </span>
      ),
    },
    {
      key: 'visit_status',
      label: 'Visit',
      render: value => (typeof value === 'string' ? humanizeValue(value) : '—'),
    },
    { key: 'patient_id', label: 'Patient' },
  ],
  // Only the dimensions the generic list honors — a status filter here would
  // render a control that does nothing (see ResourceFilterKey).
  filters: [{ key: 'patient_id', label: 'Patient id', type: 'text' }],
};
